import { logger } from '../../common/logger';
import { isDevelopmentEnvironment } from '../utils/env-utils';
import { LoginFetch } from '../../service/login-api';
import { fetchBrazeToken } from '../../service/user-profile-api';
import { Language } from '@yleisradio/areena-types';
import { Braze, ContentCard, InAppMessage } from './types';
import { inAppMessageValidator } from './InAppMessage/utils';
import { idFromUri } from '../utils/pointer';

export async function initializeBraze({
  apiKey,
  appendInAppMessage,
  baseUrl,
  braze,
  language,
  loginFetch,
  setContentCards: setContentCardsUnsafe,
  userId,
}: Arguments) {
  logger.debug('Starting braze initialization');

  const signature = await fetchBrazeToken(loginFetch).catch(err => {
    logger.error('Fetching braze token failed. Continuing without token.', err);
    return undefined;
  });
  if (!userId || !signature) {
    logger.debug(
      'No userId or signature available, skipping braze initialization'
    );
    return;
  }

  braze.initialize(apiKey, {
    baseUrl: baseUrl,
    disablePushTokenMaintenance: true,
    manageServiceWorkerExternally: true,
    enableLogging: isDevelopmentEnvironment(window.location.hostname),
    localization: language,
    enableSdkAuthentication: true,
  });

  const setContentCards = (cards: unknown) =>
    setContentCardsSafe(cards, setContentCardsUnsafe);

  const cachedContentCards = braze.getCachedContentCards();
  if (cachedContentCards?.cards) {
    setContentCards(cachedContentCards.cards);
  }

  subscribeToEvents(appendInAppMessage, braze, loginFetch, setContentCards);

  changeUser({ braze, language, userId, signature });
}

type Arguments = {
  apiKey: string;
  appendInAppMessage: (message: InAppMessage) => void;
  baseUrl: string;
  braze: Braze;
  language: Language;
  loginFetch: LoginFetch;
  setContentCards: (cards: ContentCard[]) => void;
  userId: string | undefined;
};

function setContentCardsSafe(
  maybeCards: unknown,
  setContentCards: (cards: ContentCard[]) => void
) {
  if (!Array.isArray(maybeCards)) {
    logger.warn({ maybeCards }, 'Received content cards are not in an array');
    return setContentCards([]);
  }

  const [invalidCards, validCards]: [unknown[], ContentCard[]] =
    maybeCards.reduce<[unknown[], ContentCard[]]>(
      ([notCards, cards], maybeCard) => {
        return isValidContentCard(maybeCard)
          ? [notCards, cards.concat(maybeCard)]
          : [notCards.concat(maybeCard), cards];
      },
      [[], []]
    );

  if (invalidCards.length) {
    logger.warn({ invalidCards }, 'Ignoring following invalid content cards');
  }

  setContentCards(validCards);
}

type ChangeUserParams = {
  braze: Braze;
  language: Language;
  userId: string;
  signature: string;
};
function changeUser(params: ChangeUserParams) {
  const { braze, language, userId, signature } = params;
  logger.debug('Changing user');

  braze.changeUser(userId, signature);
  braze.getUser()?.setLanguage(language);

  braze.openSession();
}

function subscribeToEvents(
  appendMessage: (message: InAppMessage) => void,
  braze: Braze,
  loginFetch: LoginFetch,
  setContentCards: (cards: unknown) => void
) {
  logger.debug('Setting up Braze subscriptions');

  braze.subscribeToSdkAuthenticationFailures(() => {
    logger.debug('Refreshing braze token.');

    fetchBrazeToken(loginFetch)
      .then(brazeToken => {
        braze.setSdkAuthenticationSignature(brazeToken);
      })
      .catch(err => {
        logger.error('Refreshing braze token failed.', err);
      });
  });

  braze.subscribeToInAppMessage((message: InAppMessage) => {
    logger.debug('In-app message received');

    const messageOK = inAppMessageValidator(message);

    if (messageOK) {
      logger.debug('In-app message was valid');
      appendMessage(message);
    }
  });

  braze.subscribeToContentCardsUpdates(event => {
    setContentCards(event.cards);
  });
}

function isValidContentCard(maybeCard: unknown): maybeCard is ContentCard {
  return (
    !!maybeCard &&
    typeof maybeCard === 'object' &&
    ['description', 'linkText', 'title'].every(param => param in maybeCard) &&
    'url' in maybeCard &&
    typeof maybeCard.url === 'string' &&
    idFromUri(maybeCard.url).length !== 0
  );
}
